import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Home, LayoutDashboard, LogOut, UserCircle2, CreditCard, Trophy, LifeBuoy, MessagesSquare, Shield, Grid3x3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import LuvmeIcon from "@/components/LuvmeIcon";
import api from "@/lib/api";
import { SupporterBadge } from "@/lib/badges";

export default function AppNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [topBadge, setTopBadge] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);

useEffect(() => {
  const load = () => api.get("/conversations/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  load();
  const t = setInterval(load, 15000);
  return () => clearInterval(t);
}, [location.pathname]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (user?.role !== "fan") return;
    api.get("/fan/badges/me").then(({ data }) => {
      const order = { Oro: 0, Argento: 1, Bronzo: 2 };
      const top = (data || []).sort((a, b) => order[a.badge] - order[b.badge])[0];
      setTopBadge(top?.badge || null);
    }).catch(() => {});
  }, [user]);

// primary items appear in the mobile bottom bar; secondary go into the "Altro" drawer
  const doLogout = async () => {
    await logout();
    navigate("/");
  };
  
  const items = [
    { to: "/feed", icon: Home, label: "Feed", testid: "nav-feed", show: true, primary: true, badge: 0 },
    { to: "/messaggi", icon: MessagesSquare, label: "Messaggi", testid: "nav-messages", show: true, primary: true, badge: unread },
    { to: "/classifica", icon: Trophy, label: "Classifica", testid: "nav-leaderboard", show: true, primary: true },
    { to: "/subscriptions", icon: CreditCard, label: "Abbonamenti", testid: "nav-subscriptions", show: user?.role === "fan", primary: true },
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", show: user?.role === "creator", primary: true },
    { to: "/profilo", icon: UserCircle2, label: "Profilo", testid: "nav-fan-profile", show: user?.role === "fan", primary: false },
    { to: "/onboarding", icon: UserCircle2, label: "Profilo", testid: "nav-profile", show: user?.role === "creator", primary: false },
    { to: "/impostazioni", icon: Shield, label: "Sicurezza", testid: "nav-settings", show: true, primary: false },
    { to: "/supporto", icon: LifeBuoy, label: "Assistenza", testid: "nav-support", show: true, primary: false },
  ].filter((i) => i.show);

  const primaryItems = items.filter((i) => i.primary);
  const secondaryItems = items.filter((i) => !i.primary);
  const isActive = (to) => location.pathname === to;

return (
  <>
  <NotificationBell />
{/* Desktop sidebar */}
  <aside className="hidden md:flex sticky top-0 h-screen w-64 shrink-0 border-r border-white/10 bg-[#0D0D0D] flex-col p-5">
  <Link to="/feed" className="flex items-center gap-2 px-2 py-4 mb-4" data-testid="nav-logo">
  <LuvmeIcon className="w-7 h-7 sm:w-8 sm:h-8" />
  <span className="text-2xl font-black font-heading tracking-tight">Luvme</span>
  </Link>
  
  <nav className="flex flex-col gap-1 flex-1">
{items.map((it) => (
  <Link
           key={it.to}
to={it.to}
data-testid={it.testid}
className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-300 font-medium ${
  isActive(it.to) ? "bg-[#E91E8C] text-white" : "text-white/70 hover:text-white hover:bg-white/5"
}`}
>
<it.icon className="w-5 h-5" />
<span>{it.label}</span>
{it.badge > 0 && <span data-testid="nav-messages-badge" className="ml-auto bg-[#E91E8C] text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{it.badge}</span>}
  </Link>
 ))}
</nav>

  <div className="border-t border-white/10 pt-4 mt-4">
  <div className="px-2 mb-3">
  <div className="flex items-center gap-2">
  <p className="text-sm font-semibold truncate">{user?.display_name}</p>
{topBadge && <SupporterBadge badge={topBadge} size="xs" />}
</div>
  <p className="text-xs text-white/50 capitalize">{user?.role}</p>
  </div>
  <Button
onClick={doLogout}
  variant="ghost"
data-testid="logout-btn"
className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 rounded-xl"
>
  <LogOut className="w-5 h-5" />
  <span>Esci</span>
  </Button>
  </div>
  </aside>

{moreOpen && (
  <div
 data-testid="mobile-more-overlay"
 className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
 onClick={() => setMoreOpen(false)}
 />
   )}

      {/* Mobile: "Altro" drawer */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-more-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-[#141414] border-t border-white/10 rounded-t-3xl p-5 pb-[calc(env(safe-area-inset-bottom)+20px)] animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#E91E8C]/15 flex items-center justify-center shrink-0">
                  <UserCircle2 className="w-5 h-5 text-[#E91E8C]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold truncate">{user?.display_name}</p>
                    {topBadge && <SupporterBadge badge={topBadge} size="xs" />}
                  </div>
                  <p className="text-[11px] text-white/50 capitalize">{user?.role}</p>
                </div>
              </div>
              <button onClick={() => setMoreOpen(false)} data-testid="more-close" aria-label="Chiudi menu" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/70 active:scale-95 transition-transform">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {secondaryItems.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`${it.testid}-more`}
                  className={`flex flex-col items-start gap-2 p-4 rounded-2xl border transition-all active:scale-95 ${
                    isActive(it.to) ? "bg-[#E91E8C] border-[#E91E8C] text-white shadow-[0_0_20px_rgba(233,30,140,0.35)]" : "bg-[#1A1A1A] border-white/10 text-white/80"
                  }`}
                >
                  <it.icon className="w-5 h-5" />
                  <span className="text-sm font-semibold">{it.label}</span>
                </Link>
              ))}
              <button
                onClick={doLogout}
                data-testid="logout-btn-mobile"
                className="flex flex-col items-start gap-2 p-4 rounded-2xl border border-red-500/20 bg-red-500/10 text-red-300 active:scale-95 transition-transform"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-semibold">Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation: primary items + Altro */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-stretch justify-around border-t border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
      >
        {primaryItems.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`${it.testid}-mobile`}
            className={`relative flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
              isActive(it.to) ? "text-[#E91E8C]" : "text-white/60"
            }`}
          >
            <it.icon className="w-5 h-5" />
            {it.badge > 0 && <span className="absolute top-1.5 right-1/2 translate-x-3 bg-[#E91E8C] text-white text-[8px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{it.badge}</span>}
            <span className="leading-none">{it.label}</span>
          </Link>
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="nav-more-mobile"
          aria-label="Altro menu"
          className={`flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${moreOpen ? "text-[#E91E8C]" : "text-white/60"}`}
        >
          <Grid3x3 className="w-5 h-5" />
          <span className="leading-none">Altro</span>
        </button>
      </nav>
    </>
  );
}
